import React, { useState, useEffect } from 'react';

/**
 * AuthenticatedImage - Component for loading images that require authentication
 *
 * @param {string} src - Image path (ex: "/img/messages/abc123.jpg")
 * @param {string} alt - Alt text
 * @param {string} className - CSS classes
 * @param {function} onClick - Click handler
 */
export const AuthenticatedImage = ({ src, alt = '', className = '', onClick, ...props }) => {
  const [imageSrc, setImageSrc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!src) {
      setLoading(false);
      return;
    }

    const loadImage = async () => {
      try {
        setLoading(true);
        setError(false);

        // Construct full URL
        const baseURL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
        const imageUrl = src.startsWith('http') ? src : `${baseURL}${src}`;

        // Fetch image with authentication
        const token = localStorage.getItem('token');
        const response = await fetch(imageUrl, {
          headers: {
            'Authorization': token ? `Bearer ${token}` : '',
          },
        });

        if (!response.ok) {
          throw new Error('Failed to load image');
        }

        // Convert to blob and create local URL
        const blob = await response.blob();
        const localUrl = URL.createObjectURL(blob);
        setImageSrc(localUrl);
        setLoading(false);
      } catch (err) {
        console.error('Error loading authenticated image:', err);
        setError(true);
        setLoading(false);
      }
    };

    loadImage();

    // Cleanup: revoke object URL when component unmounts
    return () => {
      if (imageSrc) {
        URL.revokeObjectURL(imageSrc);
      }
    };
  }, [src]);

  if (loading) {
    return (
      <div className={`authenticated-image-loading ${className}`} {...props}>
        <div className="spinner-small"></div>
      </div>
    );
  }

  if (error || !imageSrc) {
    return (
      <div className={`authenticated-image-error ${className}`} {...props}>
        <span>Failed to load image</span>
      </div>
    );
  }

  return (
    <img
      src={imageSrc}
      alt={alt}
      className={className}
      onClick={onClick}
      {...props}
    />
  );
};

/**
 * OptimizedImage - Componente para carregar imagens otimizadas
 *
 * @param {string} src - Caminho da imagem (ex: "/properties/abc123.jpg")
 * @param {number} width - Largura desejada em pixels
 * @param {number} height - Altura desejada em pixels
 * @param {string} mode - Modo de redimensionamento: 'fit', 'fill' ou 'cover'
 * @param {string} alt - Texto alternativo
 * @param {string} className - Classes CSS
 * @param {boolean} lazy - Habilitar lazy loading (padrão: true)
 */
const OptimizedImage = ({
  src,
  width,
  height,
  mode = 'fit',
  alt = '',
  className = '',
  lazy = true,
  ...props
}) => {
  // Constrói a URL com parâmetros de redimensionamento
  const buildImageUrl = () => {
    if (!src) return '';

    const params = new URLSearchParams();
    if (width) params.append('w', width);
    if (height) params.append('h', height);
    if (mode && mode !== 'fit') params.append('mode', mode);

    const queryString = params.toString();

    // Se src já começa com /img/, não adicionar novamente
    const imagePath = src.startsWith('/img/') ? src : `/img${src}`;
    return `${imagePath}${queryString ? `?${queryString}` : ''}`;
  };

  return (
    <img
      src={buildImageUrl()}
      alt={alt}
      className={className}
      loading={lazy ? 'lazy' : 'eager'}
      {...props}
    />
  );
};

export default OptimizedImage;


/**
 * ResponsiveImage - Componente para imagens responsivas com srcset
 *
 * @param {string} src - Caminho da imagem
 * @param {Array} sizes - Array de tamanhos para srcset
 * @param {string} alt - Texto alternativo
 * @param {string} mode - Modo de redimensionamento
 */
export const ResponsiveImage = ({
  src,
  sizes = [400, 800, 1200],
  alt = '',
  mode = 'fit',
  className = '',
  ...props
}) => {
  if (!src) return null;

  // Se src já começa com /img/, não adicionar novamente
  const imagePath = src.startsWith('/img/') ? src : `/img${src}`;

  // Gera srcSet
  const srcSet = sizes
    .map(width => `${imagePath}?w=${width}&mode=${mode} ${width}w`)
    .join(', ');

  // Usa o tamanho médio como fallback
  const fallbackSize = sizes[Math.floor(sizes.length / 2)];
  const fallbackSrc = `${imagePath}?w=${fallbackSize}&mode=${mode}`;

  return (
    <img
      src={fallbackSrc}
      srcSet={srcSet}
      sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
      alt={alt}
      className={className}
      loading="lazy"
      {...props}
    />
  );
};


/**
 * PropertyThumbnail - Thumbnail otimizado para cards de propriedades
 */
export const PropertyThumbnail = ({
  src,
  alt,
  width = 300,
  height = 200,
  className = '',
  ...props
}) => (
  <OptimizedImage
    src={src}
    width={width}
    height={height}
    mode="cover"
    alt={alt}
    className={className}
    {...props}
  />
);


/**
 * UserAvatar - Avatar de usuário otimizado
 */
export const UserAvatar = ({
  src,
  alt,
  size = 100,
  className = '',
  ...props
}) => (
  <OptimizedImage
    src={src}
    width={size}
    height={size}
    mode="cover"
    alt={alt}
    className={`rounded-full ${className}`}
    {...props}
  />
);


/**
 * HeroBanner - Banner hero otimizado
 */
export const HeroBanner = ({
  src,
  alt,
  height = 600,
  className = '',
  ...props
}) => (
  <OptimizedImage
    src={src}
    width={1920}
    height={height}
    mode="cover"
    alt={alt}
    className={className}
    lazy={false} // Hero images geralmente devem carregar imediatamente
    {...props}
  />
);
